import React, { useState, useEffect, useCallback } from "react";
import "./Table.scss";
import {
  Stack,
  Pagination,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { fetchTransactionsThunk } from "../../../services/MasterApi";

const RegisteredBusiness = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const transactionDetails = useSelector((state) => state.transationsData);
  const { data: tableData, pagination, isLoading } = transactionDetails;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");

  const token = sessionStorage.getItem("bb_token");
  let userId = sessionStorage.getItem("bb_userId");
  const role = sessionStorage.getItem("bb_role");
  if (role === "admin") userId = undefined;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Debounce search input by 350ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadData = useCallback(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    const params = {
      token,
      page: page,
      size: rowsPerPage,
      searchParameter: debouncedSearch,
      sortDirection: "DESC",
      filterByStatus: filterStatus,
      filterByType: filterType,
      userId: userId ? userId : "",
    };
    dispatch(fetchTransactionsThunk(params));
  }, [dispatch, token, page, rowsPerPage, debouncedSearch, filterStatus, filterType, userId, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (pagination) {
      setTotalPages(pagination.totalPages || 1);
      if (pagination.currentPage !== undefined) {
        setPage(pagination.currentPage);
      }
    }
  }, [pagination]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage - 1);
  };

  const onRowClickHandler = (row) => {
    const txId = row.businessTransactionId || row.transactionId;
    if (txId) {
      sessionStorage.setItem("bb_transactionId", txId);
      navigate(`/details/${txId}`);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setDebouncedSearch("");
    setFilterStatus("");
    setFilterType("");
    setPage(0);
  };

  return (
    <div className="min-h-[calc(100vh-72px)] bg-[#f8fafc] py-6 px-4 sm:px-8 font-sans">
      <div className="w-full space-y-4">
        {/* Filter and Control Bar */}
        <div className="bg-white rounded-xl border border-slate-200/90 p-4 shadow-2xs flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
          {/* Search Box */}
          <div className="relative flex-1 max-w-md">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400">
              <i className="fa-solid fa-magnifying-glass text-xs"></i>
            </span>
            <input
              type="text"
              className="w-full pl-9 pr-8 py-2 bg-slate-50 hover:bg-slate-100/80 focus:bg-white border border-slate-200 rounded-lg text-xs sm:text-sm text-slate-800 placeholder-slate-400 transition-colors focus:outline-none focus:ring-2 focus:ring-[#1a73e8]/20 focus:border-[#1a73e8]"
              placeholder="Search by business, owner, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-slate-400 hover:text-slate-600"
              >
                <i className="fa-solid fa-xmark text-xs"></i>
              </button>
            )}
          </div>

          {/* Filter Dropdowns & Refresh */}
          <div className="flex flex-wrap items-center gap-2.5">
            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setPage(0);
              }}
              className="px-3 py-2 bg-slate-50 hover:bg-slate-100/80 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#1a73e8]/20 focus:border-[#1a73e8] cursor-pointer"
            >
              <option value="">All Statuses</option>
              <option value="1">New</option>
              <option value="2">In-Progress</option>
              <option value="3">Approved / Completed</option>
            </select>

            {/* Entity Type Filter */}
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setPage(0);
              }}
              className="px-3 py-2 bg-slate-50 hover:bg-slate-100/80 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#1a73e8]/20 focus:border-[#1a73e8] cursor-pointer"
            >
              <option value="">All Entity Types</option>
              <option value="1">LLC</option>
              <option value="2">Corporation</option>
              <option value="3">DBA</option>
            </select>

            {/* Reset Filters button if filters active */}
            {(searchTerm || filterStatus || filterType) && (
              <button
                type="button"
                onClick={handleResetFilters}
                className="px-2.5 py-2 text-xs font-medium text-slate-600 hover:text-red-600 bg-slate-50 hover:bg-red-50 border border-slate-200 hover:border-red-200 rounded-lg transition-colors"
                title="Reset all filters"
              >
                <i className="fa-solid fa-rotate-left mr-1"></i> Reset
              </button>
            )}

            {/* Reload Data Button */}
            <button
              type="button"
              onClick={loadData}
              disabled={isLoading}
              className="px-3 py-2 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:text-[#1a73e8] transition-colors flex items-center gap-1.5 shadow-2xs"
            >
              <i className={`fa-solid fa-arrow-rotate-right text-xs ${isLoading ? "animate-spin text-[#1a73e8]" : ""}`}></i>
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Table Container */}
        <Paper
          className="table-shell"
          sx={{
            width: "100%",
            overflow: "hidden",
            borderRadius: "14px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          <TableContainer>
            {isLoading ? (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  height: "45vh",
                }}
              >
                <CircularProgress size={32} sx={{ color: "#1a73e8" }} />
                <p style={{ color: "#64748b", marginTop: "1rem", fontSize: "13px" }}>
                  Fetching registered business applications...
                </p>
              </Box>
            ) : tableData && tableData.length > 0 ? (
              <Table aria-label="registered-business-table">
                <TableHead sx={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                  <TableRow>
                    {!isMobile && (
                      <TableCell align="center" sx={{ color: "#64748b", fontWeight: 600, fontSize: "11.5px", textTransform: "uppercase", letterSpacing: "0.05em", py: 1.8 }}>
                        #
                      </TableCell>
                    )}
                    <TableCell align="left" sx={{ color: "#64748b", fontWeight: 600, fontSize: "11.5px", textTransform: "uppercase", letterSpacing: "0.05em", py: 1.8 }}>
                      App ID
                    </TableCell>
                    <TableCell align="left" sx={{ color: "#64748b", fontWeight: 600, fontSize: "11.5px", textTransform: "uppercase", letterSpacing: "0.05em", py: 1.8 }}>
                      Business Name
                    </TableCell>
                    <TableCell align="left" sx={{ color: "#64748b", fontWeight: 600, fontSize: "11.5px", textTransform: "uppercase", letterSpacing: "0.05em", py: 1.8 }}>
                      Applicant / Owner
                    </TableCell>
                    {!isMobile && (
                      <>
                        <TableCell align="left" sx={{ color: "#64748b", fontWeight: 600, fontSize: "11.5px", textTransform: "uppercase", letterSpacing: "0.05em", py: 1.8 }}>
                          Entity Type
                        </TableCell>
                        <TableCell align="left" sx={{ color: "#64748b", fontWeight: 600, fontSize: "11.5px", textTransform: "uppercase", letterSpacing: "0.05em", py: 1.8 }}>
                          Filing Date
                        </TableCell>
                        <TableCell align="left" sx={{ color: "#64748b", fontWeight: 600, fontSize: "11.5px", textTransform: "uppercase", letterSpacing: "0.05em", py: 1.8 }}>
                          Payment
                        </TableCell>
                      </>
                    )}
                    <TableCell align="center" sx={{ color: "#64748b", fontWeight: 600, fontSize: "11.5px", textTransform: "uppercase", letterSpacing: "0.05em", py: 1.8 }}>
                      Status
                    </TableCell>
                    <TableCell align="center" sx={{ color: "#64748b", fontWeight: 600, fontSize: "11.5px", textTransform: "uppercase", letterSpacing: "0.05em", py: 1.8 }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.map((row, index) => {
                    const txId = row.businessTransactionId || row.transactionId;
                    const rawStatus = (row.businessStatus || row.businessTransactionStatus || row.status || "New").trim();
                    const isNew = rawStatus.toLowerCase() === "new";
                    const isApproved = rawStatus.toLowerCase() === "approved" || rawStatus.toLowerCase() === "completed";
                    const isInProgress = rawStatus.toLowerCase() === "in-progress" || rawStatus.toLowerCase() === "in progress" || rawStatus.toLowerCase() === "inprogress";

                    let statusLabel = "New";
                    let statusBadgeClass = "status-new";
                    if (isApproved) {
                      statusLabel = "Completed";
                      statusBadgeClass = "status-completed";
                    } else if (isInProgress) {
                      statusLabel = "In-progress";
                      statusBadgeClass = "status-progress";
                    } else {
                      statusLabel = "New";
                      statusBadgeClass = "status-new";
                    }

                    const ownerName = row.ownerName || row.primaryContact?.fullName || "N/A";
                    const ownerEmail = row.primaryContact?.email;
                    const entityType = typeof row.businessType === "string"
                      ? row.businessType
                      : row.businessType?.name || row.businessType?.code || "LLC";

                    // Payment summary
                    const paymentStatusRaw = (row.paymentStatus || "").toLowerCase();
                    const isPaid =
                      paymentStatusRaw === "paid" ||
                      paymentStatusRaw === "completed" ||
                      paymentStatusRaw === "success" ||
                      Boolean(row.paymentTransactionId) ||
                      Boolean(row.businessPayment?.paymentTransactionId) ||
                      statusLabel === "Completed";
                    const totalAmt = row.totalPrice || row.businessPayment?.totalPrice;

                    return (
                      <TableRow
                        key={txId || index}
                        hover
                        sx={{
                          cursor: "pointer",
                          transition: "background 0.15s ease",
                          "&:hover": { backgroundColor: "#f8fafc" },
                        }}
                        onClick={() => onRowClickHandler(row)}
                      >
                        {!isMobile && (
                          <TableCell align="center" sx={{ color: "#94a3b8", fontSize: "12.5px" }}>
                            {page * rowsPerPage + index + 1}
                          </TableCell>
                        )}
                        <TableCell align="left" sx={{ fontWeight: 600, color: "#1a73e8", fontSize: "13px" }}>
                          {`#REGD${txId}`}
                        </TableCell>
                        <TableCell align="left">
                          <span className="font-semibold text-slate-800 text-[13.5px] block leading-snug">
                            {row.businessName || "Unnamed Business"}
                          </span>
                          {row.businessLocation?.city && (
                            <span className="text-[11px] text-slate-400">
                              {row.businessLocation.city}, {row.businessLocation.state}
                            </span>
                          )}
                        </TableCell>
                        <TableCell align="left">
                          <span className="text-slate-700 font-medium text-[13px] block leading-snug">
                            {ownerName}
                          </span>
                          {ownerEmail && (
                            <span className="text-[11px] text-slate-400 block">
                              {ownerEmail}
                            </span>
                          )}
                        </TableCell>
                        {!isMobile && (
                          <>
                            <TableCell align="left">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                                {entityType}
                              </span>
                            </TableCell>
                            <TableCell align="left" sx={{ color: "#64748b", fontSize: "12.5px" }}>
                              {formatDate(row.createDate)}
                            </TableCell>
                            <TableCell align="left">
                              {isPaid ? (
                                <div className="flex flex-col items-start gap-1">
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11.5px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                    <i className="fa-solid fa-check text-[10px]"></i>
                                    <span>Paid</span>
                                  </span>
                                  {totalAmt ? (
                                    <span className="text-[11.5px] text-slate-600 font-semibold pl-1">
                                      ${parseFloat(totalAmt).toFixed(2)}
                                    </span>
                                  ) : null}
                                </div>
                              ) : (
                                <div className="flex flex-col items-start gap-1">
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11.5px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                    <i className="fa-regular fa-clock text-[10px]"></i>
                                    <span>Pending</span>
                                  </span>
                                  {totalAmt ? (
                                    <span className="text-[11px] text-slate-400 font-normal pl-1">
                                      Due: ${parseFloat(totalAmt).toFixed(2)}
                                    </span>
                                  ) : null}
                                </div>
                              )}
                            </TableCell>
                          </>
                        )}
                        <TableCell align="center">
                          <span className={`status-pill ${statusBadgeClass}`}>
                            {statusLabel}
                          </span>
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => onRowClickHandler(row)}
                            className="bg-blue-50 hover:bg-blue-100/80 text-[#1a73e8] border border-blue-100 rounded-lg px-2.5 py-1 text-xs font-semibold inline-flex items-center gap-1 transition-colors cursor-pointer"
                          >
                            <span>View</span>
                            <i className="fa-solid fa-arrow-right text-[10px]"></i>
                          </button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-3">
                  <i className="fa-regular fa-folder-open text-2xl"></i>
                </div>
                <h3 className="text-base font-semibold text-slate-800 mb-1">
                  No registration applications found
                </h3>
                <p className="text-xs text-slate-500 max-w-sm mb-4">
                  {searchTerm || filterStatus || filterType
                    ? "No businesses matched your search query or filter selection."
                    : "No registered business applications have been recorded in the database yet."}
                </p>
                {(searchTerm || filterStatus || filterType) && (
                  <button
                    type="button"
                    onClick={handleResetFilters}
                    className="px-3.5 py-1.5 bg-[#1a73e8] hover:bg-[#1557b0] text-white text-xs font-semibold rounded-lg shadow-2xs transition-colors"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            )}
          </TableContainer>

          {/* Pagination */}
          <div className="p-3 sm:p-4 bg-white border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
            <span className="text-xs text-slate-500">
              Showing page <strong className="text-slate-700">{page + 1}</strong> of{" "}
              <strong className="text-slate-700">{totalPages}</strong> ({pagination?.totalRows || 0} total applications)
            </span>
            <Stack spacing={2}>
              <Pagination
                count={totalPages}
                page={page + 1}
                color="primary"
                onChange={handleChangePage}
                size="small"
                sx={{
                  "& .Mui-selected": {
                    backgroundColor: "#1a73e8 !important",
                    color: "#ffffff",
                  },
                }}
              />
            </Stack>
          </div>
        </Paper>
      </div>
    </div>
  );
};

export default RegisteredBusiness;
